import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Carousel from 'nuka-carousel';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import throttle from 'lodash/throttle';

import { DateTimeService } from '@shared/services/DateTimeService';
import media from '../../utils/mediaQueries';

import arrow_left from '../../images/arrow-left.svg';
import arrow_right from '../../images/arrow-right.svg';

import {
  FONT_FAMILY_MAIN, LIGHT_BLUE, TEXT_COLOR, MAIN_BLUE,
} from '../../theme';

const Header = styled.div`
  position: relative;
  height: 42px;
  margin-bottom: 8px;
`;

const HeaderSlotContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 42px;
  
  .slider-container {
    width: 100%;
    padding: 0 12px;
  }
`;

const HeaderSlot = styled.div`
  padding: 7px 10px;
  font-family: ${FONT_FAMILY_MAIN};
  font-size: 12px;
  line-height: 1.17;
  letter-spacing: 0.1px;
  text-align: center;
  cursor: pointer;
  width: 82px;
  ${(props) => props.active
    ? `
    font-weight: bold;
    color: ${TEXT_COLOR}
    border-radius: 6px;
    background-color: #f4f7f9;
  `
    : `
    &:hover {
      opacity: 0.8;
    }
    color: #979ea7;
  `}
`;

const NavButton = styled.div`
  ${(props) => (props.direction === 'left' ? 'margin-left: -8px;' : 'margin-right: -8px;')}
  width: 20px;
  height: 20px;
  border-radius: 50%;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
  background-color: #597cbd;
  display: none;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    opacity: 0.8;
  }
  ${(props) => props.isNavVisible
    && `
    display: flex;
  `}
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 8px;
  margin-bottom: 16px;
`;

const Slot = styled.div`
  padding: 7px 9px;
  border-radius: 6px;
  border: solid 1px ${LIGHT_BLUE};
  font-size: 14px;
  text-align: center;
  cursor: pointer;
  letter-spacing: -0.2px;
  color: ${(props) => (props.active ? '#ffffff' : MAIN_BLUE)};
  ${(props) => props.active
    ? `
    font-weight: 600;
    background-color: ${MAIN_BLUE};
    border-color: ${MAIN_BLUE};
    ` : `
    &:hover {
    color: ${MAIN_BLUE};
    border-color: ${MAIN_BLUE};
    font-weight: 600;
    }
  `}
  transition: all .2s;
  ${media.mobile`
    width: auto;
  `}
`;

const Image = styled.img`
  width: 16px;
  height: 16px;
`;

const EmptyText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
  height: 150px;
  font-family: ${FONT_FAMILY_MAIN};
  font-size: 16px;
  line-height: 1.19;
  letter-spacing: -0.1px;
  color: ${TEXT_COLOR};
`;

const CAROUSEL_ANIMATION_SPEED = 600;

class MeetingsCalendar extends Component {
  onNavClick = throttle((cb) => cb(), CAROUSEL_ANIMATION_SPEED + 150);

  renderSlotHeader = (date, index) => {
    const { currentDate, onHeaderSlotClick } = this.props;
    const slotStartDate = new Date(date);
    const { setDateLocale } = DateTimeService;

    return (
      <HeaderSlot
        key={`meeting-header-slot${index}`}
        active={currentDate === date}
        onClick={() => onHeaderSlotClick(date)}
      >
        <div>{setDateLocale(slotStartDate, 'EEEE')}</div>
        <div>{setDateLocale(slotStartDate, 'd MMM')}</div>
      </HeaderSlot>
    );
  };

  render() {
    const {
      dates, slots, currentTime, lastSlideIndex, onSlideChanged, onSlotClick, timeZone,
    } = this.props;

    return (
      <>
        { !isEmpty(dates) && (
          <Header>
            <HeaderSlotContainer>
              <Carousel
                autoplay={false}
                slidesToShow={3}
                slidesToScroll={3}
                speed={CAROUSEL_ANIMATION_SPEED}
                slideIndex={lastSlideIndex}
                slideWidth="82px"
                afterSlide={(slideIndex) => onSlideChanged(slideIndex)}
                renderBottomCenterControls={null}
                renderCenterLeftControls={({ previousSlide }) => (
                  <NavButton
                    direction="left"
                    isNavVisible
                    onClick={() => this.onNavClick(previousSlide)}
                  >
                    <Image src={arrow_left} alt="Précédent" />
                  </NavButton>
                )}
                renderCenterRightControls={({ nextSlide }) => (
                  <NavButton
                    direction="right"
                    isNavVisible
                    onClick={() => this.onNavClick(nextSlide)}
                  >
                    <Image src={arrow_right} alt="Suivant" />
                  </NavButton>
                )}
              >
                { map(dates, (date, index) => this.renderSlotHeader(date, index)) }
              </Carousel>
            </HeaderSlotContainer>
          </Header>
        )}
        {
          isEmpty(slots) ? (
            <EmptyText>Oups. Il ne reste plus de créneau disponible aujourd’hui. Merci de choisir une autre date.</EmptyText>
          ) : (
            <Content>
              {
                map(slots, (slot, yindex) => (
                  <Slot
                    key={`meeting-slot${yindex}`}
                    active={currentTime === slot}
                    onClick={() => onSlotClick(slot)}
                  >
                    {DateTimeService.formatInTimeZone(new Date(slot), timeZone, 'HH:mm')}
                  </Slot>
                ))
              }
            </Content>
          )
        }
      </>
    );
  }
}

MeetingsCalendar.propTypes = {
  dates: PropTypes.array,
  slots: PropTypes.array,
  // Date in ISOstring format
  currentDate: PropTypes.string,
  // Date in ISOstring format
  currentTime: PropTypes.string,
  lastSlideIndex: PropTypes.number,
  onHeaderSlotClick: PropTypes.func,
  onSlotClick: PropTypes.func,
  onSlideChanged: PropTypes.func,
  timeZone: PropTypes.string.isRequired,
};

MeetingsCalendar.defaultProps = {
  dates: [],
  slots: [],
  currentDate: '',
  currentTime: '',
  lastSlideIndex: 0,
  onHeaderSlotClick: () => {},
  onSlotClick: () => {},
  onSlideChanged: () => {},
};

export default MeetingsCalendar;
